import React, { useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { ThemeProvider } from './context/ThemeContext';
import { SocketProvider } from './context/SocketContext';
import { AuthModalProvider } from './context/AuthModalContext';
import ErrorBoundary from './components/common/ErrorBoundary';
import AppRoutes from './routes/AppRoutes';
import LoginModal from './components/common/LoginModal';
import { TOAST_DURATION } from './constants';
import useAuthStore from './store/authStore';
import { getMeApi } from './api/auth.api';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, staleTime: 5 * 60 * 1000 },
  },
});

// Syncs fresh user data (including role) from server on every app load.
// This ensures localStorage never serves stale role/profile data.
const AuthSync = () => {
  const { isAuthenticated, _hydrated, updateUser, logout } = useAuthStore();

  useEffect(() => {
    if (!_hydrated || !isAuthenticated) return;
    getMeApi()
      .then(({ data }) => updateUser(data.data))
      .catch(() => logout()); // token invalid/expired — force logout
  }, [_hydrated]);

  return null;
};

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <BrowserRouter>
          <AuthModalProvider>
            <SocketProvider>
              <AuthSync />
              <AppRoutes />
              <LoginModal />
              <ToastContainer position="top-right" autoClose={TOAST_DURATION} />
            </SocketProvider>
          </AuthModalProvider>
        </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
